import { DeleteIcon } from "@chakra-ui/icons";
import { Box, Button, Flex, Grid, Input, Text } from "@chakra-ui/react";
import apiCarteira from "api/api-carteira";
import { Dropdown } from "components/dropdown";
import { DropdownField } from "components/dropdown-field";
import { FormTitle } from "components/form-title";
import { InputField } from "components/input-field";
import { InputTag } from "components/servicos-components/input-tag";
import { SkeletonLoading } from "components/skeleton-loading";
import { Toast } from "components/toast";
import { useEffect, useRef, useState } from "react";
import { FaSave } from "react-icons/fa";
import { useRouter } from "routes/router-control/use-router";
import { isObjectEqual } from "utils/object-methods";
import {
  cnpjMask,
  maskPositiveNumber,
  phoneFormatMask,
} from "utils/string-formats";

interface SessionProps {
  nomeEmpresa?: string;
  website?: string;
  nomeIA?: string;
  cnpj?: string;
  anosAtuacao?: string;
  telefoneEmergencia?: string;
  followUp?: string;
  etapasDerivacaoHumano?: string[];
  modeloPrimeiraMensagem?: string;
  perfilCliente?: string;
  qualificacaoLeadOpcoes?: string[];
  qualificacaoLeadModeloMsg?: string;
  telefoneNotificacoes?: string;
  reworkMailingOptions?: string[];
}

interface OpenModalCreateIASessionParams {}

export function IAForm() {
  let [formFields, setFormFields] = useState<SessionProps>({});
  const [loading, setLoading] = useState(false);
  const [isSaveLoading, setIsSaveLoading] = useState(false);

  const router = useRouter();
  const savedObject = useRef<Partial<SessionProps>>({});
  const isSaveButtonDisabled = isObjectEqual(formFields, savedObject.current);

  const params = new URLSearchParams(window.location.search);
  const customerId = params.get("partnerId");

  const handleSubmit = async () => {
    setIsSaveLoading(true);
    try {
      const { data } = await apiCarteira.put(
        `/service-vendeai/config?customer_id=${customerId}`,
        formFields
      );
      savedObject.current = data;
      setFormFields(data);
    } catch (e) {
      Toast({ title: `Erro ao atualizar dados` });
    } finally {
      setIsSaveLoading(false);
    }
  };

  const getData = async () => {
    setLoading(true);
    try {
      let { data } = await apiCarteira.get(
        `/service-vendeai/config?customer_id=${customerId}`
      );
      if (data.qualificacaoLeadOpcoes) {
        data.qualificacaoLeadOpcoes = data.qualificacaoLeadOpcoes.filter(
          (crr: string) =>
            opcoesQualificacaoLead.find(({ value }) => value === crr)
        );
      }
      savedObject.current = data;
      setFormFields(data);
    } catch (e: any) {
      const errorMessage =
        e?.response?.data?.msgInterface ?? e?.response?.data?.message;
      Toast({ title: `Erro ao obter dados`, description: errorMessage });
    } finally {
      setLoading(false);
    }
  };

  const onChangeField = (k: keyof SessionProps, value: any) => {
    formFields = { ...formFields, [k]: value };
    setFormFields((prev) => formFields);
  };

  const onChangeMultiselect = (field: keyof SessionProps, value: any) => {
    const currentEl = formFields[field] as any[] | null;
    console.log(currentEl);
    if (currentEl != null && currentEl?.length) {
      const isInclude = currentEl.includes(value);
      if (isInclude) {
        onChangeField(
          field,
          currentEl.filter((curr: any) => curr !== value)
        );
      } else onChangeField(field, [...formFields[field]!, value]);
    } else onChangeField(field, [value]);
  };

  const discardChanges = () => {
    setFormFields(savedObject.current);
  };

  useEffect(() => {
    if (!customerId) router.push(`/servicos`);
    getData();
  }, []);

  return (
    <Box
      maxW="580px"
      mx="auto"
      p="20px 15px"
      borderRadius="4px"
      w="100%"
      bg="#fff"
    >
      <FormTitle>Dados da Empresa:</FormTitle>
      <Grid templateColumns="1fr" gap="20px">
        <SkeletonLoading isLoading={loading} display="block" borderRadius="6px">
          <InputField
            title="Nome da Empresa"
            value={formFields.nomeEmpresa || ""}
            onChange={(e) => onChangeField("nomeEmpresa", e.target.value)}
          />
        </SkeletonLoading>
        <SkeletonLoading isLoading={loading} display="block" borderRadius="6px">
          <InputField
            title="Website"
            value={formFields.website || ""}
            onChange={(e) => onChangeField("website", e.target.value)}
          />
        </SkeletonLoading>
        <SkeletonLoading isLoading={loading} display="block" borderRadius="6px">
          <Text mb="5px" fontWeight="medium">
            Nome da IA
          </Text>
          <Text color="gray.500" mb="8px" fontSize="12px" fontStyle="italic">
            Padrão: Natália
          </Text>
          <InputField
            value={formFields.nomeIA || ""}
            onChange={(e) => onChangeField("nomeIA", e.target.value)}
          />
        </SkeletonLoading>
        <SkeletonLoading isLoading={loading} display="block" borderRadius="6px">
          <InputField
            title="CNPJ"
            value={cnpjMask(formFields.cnpj).value || ""}
            onChange={(e) =>
              onChangeField("cnpj", cnpjMask(e.target.value).raw)
            }
          />
        </SkeletonLoading>
        <SkeletonLoading isLoading={loading} display="block" borderRadius="6px">
          <InputField
            title="Anos de Atuação"
            value={maskPositiveNumber(formFields.anosAtuacao).value || ""}
            onChange={(e) =>
              onChangeField(
                "anosAtuacao",
                maskPositiveNumber(e.target.value).raw
              )
            }
          />
        </SkeletonLoading>

        <FormTitle mt="12px">Configurações de Fluxo:</FormTitle>

        <SkeletonLoading isLoading={loading} display="block" borderRadius="6px">
          <Text mb="5px" fontWeight="medium">
            Telefone de Emergência
          </Text>
          <Text color="gray.500" mb="8px" fontSize="12px" fontStyle="italic">
            Esse telefone será passado aos clientes em casos críticos de
            insatisfação
          </Text>
          <InputField
            value={phoneFormatMask(formFields.telefoneEmergencia).value || ""}
            onChange={(e) =>
              onChangeField(
                "telefoneEmergencia",
                phoneFormatMask(e.target.value).raw
              )
            }
          />
        </SkeletonLoading>
        <SkeletonLoading isLoading={loading} display="block" borderRadius="6px">
          <Text mb="5px" fontWeight="medium">
            Configurações de follow-up
          </Text>
          <Text color="gray.500" mb="8px" fontSize="12px" fontStyle="italic">
            O follow-up é a mensagem enviada no caso do cliente não responder a
            pergunta da IA. O primeiro follow-up é enviado depois de 10 minutos
            e o segundo fora do horário comercial
          </Text>
          <DropdownField
            value={formFields.followUp || ""}
            options={[
              {
                name: "1 Follow-up derivando para humano sem resposta",
                value: "ONE_FUP_HUMAN",
              },
              {
                name: "2 Follow-ups derivando para humano sem resposta",
                value: "TWO_FUP_HUMAN",
              },
              {
                name: "2 Follow-ups sem derivação para humano",
                value: "TWO_FUP_AUTO",
              },
            ]}
            onChange={(value) => onChangeField("followUp", value)}
          />
        </SkeletonLoading>
        <SkeletonLoading isLoading={loading} display="block" borderRadius="6px">
          <Text mb="5px" fontWeight="medium">
            Etapas de Derivação para Humano
          </Text>
          <Text color="gray.500" mb="8px" fontSize="12px" fontStyle="italic">
            Defina as etapas nas quais os casos serão derivados para humanos
          </Text>
          <DropdownField
            value={formFields.etapasDerivacaoHumano ?? []}
            options={etapasDerivacaoHumanoOptions}
            onChange={(value) =>
              onChangeMultiselect("etapasDerivacaoHumano", value)
            }
            dropdownProps={{
              multiSelect: true,
              children: etapasDerivacaoHumanoOptions
                ?.filter(({ value }) =>
                  formFields.etapasDerivacaoHumano?.includes(value)
                )
                .map(({ name }) => name)
                .join(", "),
            }}
          />
        </SkeletonLoading>
        <SkeletonLoading isLoading={loading} display="block" borderRadius="6px">
          <Text mb="5px" fontWeight="medium">
            Modelo Primeira Mensagem
          </Text>
          <Text color="gray.500" mb="8px" fontSize="12px" fontStyle="italic">
            Defina um modelo para sua mensagem inicial. Por exemplo, para
            destacar algum diferencial da sua loja!
          </Text>
          <Input
            value={formFields.modeloPrimeiraMensagem || ""}
            placeholder={`Exemplo: Que bom que você nos chamou!! Normalmente pagamos mais do que qualquer outra instituição financeira. Bora atualizar a consulta pra ver na prática`}
            as="textarea"
            h="80px"
            pt="4px"
            _placeholder={{ color: "gray.400" }}
            onChange={(e) =>
              onChangeField("modeloPrimeiraMensagem", e.target.value)
            }
          />
        </SkeletonLoading>
        <SkeletonLoading isLoading={loading} display="block" borderRadius="6px">
          <Text mb="5px" fontWeight="medium">
            Perfil do Cliente
          </Text>
          <Text color="gray.500" mb="8px" fontSize="12px" fontStyle="italic">
            Defina o perfil do cliente para as mensagens recebidas no número,
            <br />
            - Padrão (use essa configuração se você trabalha com tráfego ou
            novos clientes)
            <br />- Tomador (use essa configuração se você trabalha com base ou
            retrabalho de carteira)
          </Text>
          <DropdownField
            value={formFields.perfilCliente || ""}
            options={[
              { name: "Padrão", value: "DEFAULT" },
              { name: "Tomador", value: "TOMADOR_FORA_CARTEIRA" },
            ]}
            onChange={(value) => onChangeField("perfilCliente", value)}
          />
        </SkeletonLoading>
        <SkeletonLoading isLoading={loading} display="block" borderRadius="6px">
          <FormTitle mt="12px">Qualificação de Lead:</FormTitle>
          <Text color="gray.500" mb="8px" fontSize="12px" fontStyle="italic">
            Mensagem inicial para entender o produto/convênio que o cliente se
            adequa
          </Text>
        </SkeletonLoading>
        <SkeletonLoading isLoading={loading} display="block" borderRadius="6px">
          <Text mb="5px" fontWeight="medium">
            Opções
          </Text>
          <Text color="gray.500" mb="8px" fontSize="12px" fontStyle="italic">
            Defina as opções (Ex.: FGTS, CLT, Crédito Pessoal, SIAPE)
          </Text>
          <DropdownField
            options={opcoesQualificacaoLead}
            value={formFields.qualificacaoLeadOpcoes ?? []}
            onChange={(value) =>
              onChangeMultiselect("qualificacaoLeadOpcoes", value)
            }
            dropdownProps={{
              multiSelect: true,
              children: formFields.qualificacaoLeadOpcoes
                ?.filter((crr) =>
                  opcoesQualificacaoLead.map(({ value }) => value).includes(crr)
                )
                .map(
                  (crr) =>
                    opcoesQualificacaoLead.find(({ value }) => value === crr)
                      ?.name
                )
                .join(", "),
            }}
          />
        </SkeletonLoading>
        <SkeletonLoading isLoading={loading} display="block" borderRadius="6px">
          <Text mb="5px" fontWeight="medium">
            Modelo Mensagem de Qualificação
          </Text>
          <Text color="gray.500" mb="8px" fontSize="12px" fontStyle="italic">
            Defina a mensagem
          </Text>
          <Input
            value={formFields.qualificacaoLeadModeloMsg || ""}
            placeholder={`Olá, tudo bem? \n\nPara melhor te atender, me conta qual produto você tem interesse?`}
            as="textarea"
            h="95px"
            pt="4px"
            _placeholder={{ color: "gray.400" }}
            onChange={(e) =>
              onChangeField("qualificacaoLeadModeloMsg", e.target.value)
            }
          />
        </SkeletonLoading>
        <SkeletonLoading isLoading={loading} display="block" borderRadius="6px">
          <Text mb="5px" fontWeight="medium">
            Retrabalho de Carteira Automático
          </Text>
          <Text color="gray.500" mb="8px" fontSize="12px" fontStyle="italic">
            Defina as opções para o retrabalho automático: <br />
            &nbsp;- Aniversariante: todo dia 02, ativa para aniversariantes do
            mês passado.
            <br />
            &nbsp;- Sem saldo: todo dia 20, ativa para quem ficou sem saldo no
            último mês.
            <br />
            &nbsp;- Com saldo na última semana hot: todo dia 20, ativa para quem
            teve saldo na última semana hot.
            <br /> <br />
            Obs.: retrabalhos individuais podem ser desativados ao remover o
            atendimento da etapa "Retrabalho agendado" no funil.
          </Text>
          <DropdownField
            options={reworkMailingOptions}
            value={formFields.reworkMailingOptions ?? []}
            onChange={(value) =>
              onChangeMultiselect("reworkMailingOptions", value)
            }
            dropdownProps={{
              multiSelect: true,
              children: formFields.reworkMailingOptions
                ?.filter((crr) =>
                  reworkMailingOptions.map(({ value }) => value).includes(crr)
                )
                .map(
                  (crr) =>
                    reworkMailingOptions.find(({ value }) => value === crr)
                      ?.name
                )
                .join(", "),
            }}
          />
        </SkeletonLoading>
        <FormTitle mt="12px">Configurações Gerais:</FormTitle>
        <SkeletonLoading isLoading={loading} display="block" borderRadius="6px">
          <Text mb="5px" fontWeight="medium">
            Telefone de Notificações
          </Text>
          <Text color="gray.500" mb="8px" fontSize="12px" fontStyle="italic">
            Receba notificações e resumos diários sobre as vendas da IA
          </Text>
          <InputField
            value={phoneFormatMask(formFields.telefoneNotificacoes).value || ""}
            onChange={(e) =>
              onChangeField(
                "telefoneNotificacoes",
                phoneFormatMask(e.target.value).raw
              )
            }
          />
        </SkeletonLoading>
        <Flex justifyContent="flex-end">
          {!isSaveButtonDisabled ? (
            <Button
              onClick={() => discardChanges()}
              variant="outline"
              leftIcon={<DeleteIcon />}
              mr="8px"
            >
              Descartar Alterações
            </Button>
          ) : null}
          <Button
            onClick={() => handleSubmit()}
            isLoading={isSaveLoading}
            loadingText="Salvando"
            isDisabled={isSaveButtonDisabled}
            leftIcon={<FaSave />}
          >
            Salvar
          </Button>
        </Flex>
      </Grid>
    </Box>
  );
}

const etapasDerivacaoHumanoOptions = [
  { name: "Problema na simulação", value: "SIMULATION_FAIL" },
  {
    name: "Não optante",
    value: "DID_NOT_ADHERE_SAQUE_ANIVERSARIO",
  },
  { name: "Cliente com saldo", value: "SIMULATION_SUCCESS" },
  { name: "Ticket alto", value: "HIGH_TICKET" },
  { name: "Contrato digitado", value: "DIGITADOR" },
];

const opcoesQualificacaoLead = [
  { name: "FGTS", value: "fgts" },
  { name: "Empréstimo CLT", value: "clt" },
  { name: "Crédito Pessoal", value: "credito_pessoal" },
  { name: "Auxílio Brasil", value: "auxilio_brasil" },
  { name: "Crédito na Conta de Luz", value: "credito_luz" },
  { name: "INSS", value: "inss" },
  { name: "SIAPE", value: "siape" },
  { name: "Prefeituras", value: "prefeituras" },
  { name: "Governos", value: "governos" },
];

const reworkMailingOptions = [
  { name: "Aniversariante", value: "birthday" },
  { name: "Sem saldo", value: "no_balance" },
  { name: "Com saldo", value: "balance_in_last_hot_week" },
];
